import {
  colorStopToString,
  calculateRadialStop,
  fixTransparentColorStops,
} from '../color-utils.js';

/**
 * Calculate radius based on extent keyword for radial gradients with custom position
 * @param {string} extent - Extent keyword (closest-side, farthest-side, closest-corner, farthest-corner)
 * @param {boolean} isCircle - Whether the gradient is a circle
 * @param {number} distToLeft - Distance from center to left edge
 * @param {number} distToRight - Distance from center to right edge
 * @param {number} distToTop - Distance from center to top edge
 * @param {number} distToBottom - Distance from center to bottom edge
 * @returns {number} Calculated radius
 */
function calculateExtentRadiusFromPosition(
  extent,
  isCircle,
  distToLeft,
  distToRight,
  distToTop,
  distToBottom
) {
  // Calculate distances to corners
  const distToTopLeft = Math.sqrt(distToLeft * distToLeft + distToTop * distToTop);
  const distToTopRight = Math.sqrt(distToRight * distToRight + distToTop * distToTop);
  const distToBottomLeft = Math.sqrt(distToLeft * distToLeft + distToBottom * distToBottom);
  const distToBottomRight = Math.sqrt(distToRight * distToRight + distToBottom * distToBottom);

  switch (extent) {
    case 'closest-side':
      return Math.min(distToLeft, distToRight, distToTop, distToBottom);
    case 'farthest-side':
      return Math.max(distToLeft, distToRight, distToTop, distToBottom);
    case 'closest-corner':
      return Math.min(distToTopLeft, distToTopRight, distToBottomLeft, distToBottomRight);
    case 'farthest-corner':
    default:
      // Default for CSS radial-gradient is farthest-corner
      return Math.max(distToTopLeft, distToTopRight, distToBottomLeft, distToBottomRight);
  }
}

/**
 * Parse position value to pixel coordinate
 * @param {Object} posValue - Position value object { type, value }
 * @param {number} size - Size of the dimension (width or height)
 * @param {number} offset - Offset (x or y)
 * @returns {number} Pixel coordinate
 */
function parsePositionValue(posValue, size, offset) {
  if (!posValue) return offset + size / 2; // Default to center

  if (posValue.type === '%') {
    return offset + (parseFloat(posValue.value) / 100) * size;
  } else if (posValue.type === 'px') {
    return offset + parseFloat(posValue.value);
  } else if (posValue.type === 'position-keyword') {
    const keyword = posValue.value.toLowerCase();
    switch (keyword) {
      case 'left':
      case 'top':
        return offset;
      case 'right':
      case 'bottom':
        return offset + size;
      case 'center':
      default:
        return offset + size / 2;
    }
  }

  return offset + size / 2; // Default to center
}

/**
 * Render a radial gradient
 * @param {CanvasRenderingContext2D} context - Canvas context
 * @param {Object} gradientDef - Gradient definition from parser
 * @param {Object} bounds - Bounding box { x, y, w, h }
 * @returns {CanvasGradient} Canvas gradient object
 */
export function renderRadialGradient(context, gradientDef, { x, y, w, h }) {
  // Parse radial gradient shape and size from orientation
  const orientation = gradientDef.orientation
    ? gradientDef.orientation[0]
    : { type: 'shape', value: 'ellipse' };

  const isCircle = orientation?.type === 'shape' && orientation.value === 'circle';

  // Get extent keyword if specified, default to 'farthest-corner' per CSS spec
  let extentKeyword = 'farthest-corner';
  let explicitRadius = null;

  if (orientation?.style) {
    if (orientation.style.type === 'extent-keyword') {
      extentKeyword = orientation.style.value;
    } else if (orientation.style.type === 'px') {
      explicitRadius = parseFloat(orientation.style.value);
    } else if (orientation.style.type === '%') {
      // Percentage is relative to the gradient box size
      explicitRadius = (parseFloat(orientation.style.value) / 100) * Math.max(w, h);
    }
  }

  // Parse position from "at" clause
  const atPosition = orientation?.at?.value;
  const centerX = parsePositionValue(atPosition?.x, w, x);
  const centerY = parsePositionValue(atPosition?.y, h, y);

  // Calculate distances from center to edges for extent calculation
  const distToLeft = centerX - x;
  const distToRight = x + w - centerX;
  const distToTop = centerY - y;
  const distToBottom = y + h - centerY;

  // Calculate radius based on extent keyword and actual position
  let radius;
  if (explicitRadius !== null) {
    radius = explicitRadius;
  } else {
    radius = calculateExtentRadiusFromPosition(
      extentKeyword,
      isCircle,
      distToLeft,
      distToRight,
      distToTop,
      distToBottom
    );
  }

  // Create radial gradient with inner and outer circles
  const gradient = context.createRadialGradient(
    centerX,
    centerY,
    0, // Start circle (center)
    centerX,
    centerY,
    radius // End circle
  );

  // Clone colorStops to avoid mutating the original
  const colorStops = gradientDef.colorStops.map((stop) => ({ ...stop }));

  // Fix transparent color stops to match CSS premultiplied alpha behavior
  fixTransparentColorStops(colorStops);

  // Add color stops based on the gradient definition
  let lastPosition = 0;
  let lastColorValue = null;

  colorStops.forEach((stop, index) => {
    const position = calculateRadialStop(stop, index, radius);
    const colorValue = colorStopToString(stop);

    if (colorValue) {
      const clampedPosition = Math.max(0, Math.min(1, position));
      gradient.addColorStop(clampedPosition, colorValue);
      lastPosition = clampedPosition;
      lastColorValue = colorValue;
    }
  });

  // If the last color stop is not at 100%, extend it to 100%
  // This matches CSS behavior where the last color continues to the edge
  if (lastPosition < 1 && lastColorValue) {
    gradient.addColorStop(1, lastColorValue);
  }

  return gradient;
}
